/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import "./App.css";

import ControlTray from "./components/control-tray/ControlTray";
import { TopBar } from "./components/TopBar2";
import { useEffect, useMemo, useState } from "react";
import TAAudioAgent, { RecommendationData } from "ta-agent-sdk";
import { useTranscriptMessages } from "ta-agent-sdk/react";
import { Map } from "./components/Map";
import { useMaps } from "./contexts/MapsContext";
// Recommendation list is now rendered inside Map
import * as amplitude from "@amplitude/analytics-browser";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { autocapturePlugin } from "@amplitude/plugin-autocapture-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
// import QRCode from "react-qr-code";
import { useTranslation } from "react-i18next";
import ChatBox from "./components/ChatBox";

const sessionReplayTracking = sessionReplayPlugin();
amplitude.add(sessionReplayTracking);
amplitude.add(autocapturePlugin());
amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY!, {
  sessionId: parseInt(Math.random().toString().substring(10)),
  autocapture: {
    elementInteractions: true,
  },
});

const resolveRandomId = (): string => {
  const cryptoImpl: Crypto | undefined =
    typeof globalThis !== "undefined"
      ? (globalThis.crypto as Crypto | undefined)
      : undefined;
  if (cryptoImpl?.randomUUID) {
    return cryptoImpl.randomUUID();
  }
  return `msg_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};

function App() {
  const { t, i18n } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(true);
  const [speakerMuted, setSpeakerMuted] = useState(true);
  const [recommendations, setRecommendations] = useState<RecommendationData[]>(
    [],
  );

  const agent = useMemo(() => {
    return new TAAudioAgent({
      backendHost: process.env.REACT_APP_BACKEND_HOST!,
      agent: "lasvegas",
      onAnalytics(event, payload) {
        if (event === "agent_start") {
          amplitude.setUserId(payload["sessionId"]);
        }
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({ event, ...payload });
        }
        try {
          amplitude.track(event, payload);
        } catch (e) {
          console.error("Amplitude tracking error:", e);
        }
      },
      initialMessage: `[Session started, language:${i18n.language}]`,
      inactivityCheck: true,
      autoMute: process.env.REACT_APP_AUTO_MUTE === "true",
      speakerMuted: true,
    });
  }, [i18n.language]);

  const { set3D, closeDetails, openDetails } = useMaps();
  const { messages, appendMessage } = useTranscriptMessages({
    agent,
  });

  const [showChat, setShowChat] = useState(true);
  const [showControlTray, setShowControlTray] = useState(true);

  async function fetchPhotos(data: RecommendationData[]) {
    const updatedRecommendations = await Promise.all(
      data.map(async (rec) => {
        const propertyId =
          "location" in rec ? rec.location?.id : rec.experience?.id;
        if (propertyId && !rec.photos) {
          try {
            const response = await fetch(
              `https://l4detuz832.execute-api.us-east-1.amazonaws.com/dev/photo?id=${propertyId}`,
            );
            const photos = await response.json();
            return { ...rec, photos };
          } catch (error) {
            console.error("Failed to fetch photos for recommendation:", error);
            return rec;
          }
        }
        return rec;
      }),
    );
    setRecommendations(updatedRecommendations);
  }
  useEffect(() => {
    const goBack = () => {
      closeDetails();
    };
    const switchPlace = ({ tripadvisor_id }: { tripadvisor_id: number }) => {
      const recommendation = recommendations.find((rec) => {
        const id =
          "location" in rec
            ? rec.location?.id
            : rec.experience?.id;
        return id === tripadvisor_id;
      });
      if (recommendation) {
        openDetails(recommendation, false);
        return;
      }
    };
    const switchRecommendations = (data: RecommendationData[]) => {
      setRecommendations(data);
      // Close any open details and force map back to 2D on recommendation change
      closeDetails();
      set3D(false);
      fetchPhotos(data);
      // Map component manages its own list scroll state
    };
    agent.onConnected = (connected) => {
      setConnected(connected);
      if (!connected) {
        window.location.reload();
      }
    };

    agent.onMicStateChange = (muted) => {
      setMuted(muted);
    };
    agent.onSpeakerStateChange = (muted) => {
      setSpeakerMuted(muted);
    };
    agent.onEvent("goBack", goBack);
    agent.onEvent("switchPlace", switchPlace);
    agent.onEvent("switchRecommendations", switchRecommendations);

    return () => {
      agent.onEvent("goBack", goBack);
      agent.offEvent("switchPlace", switchPlace);
      agent.offEvent("switchRecommendations", switchRecommendations);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, closeDetails, openDetails, set3D]);

  return (
    <div className="App">
      <div className="streaming-console">
        {!connected ? (
          <img
            alt="Las Vegas"
            className="absolute object-cover h-full w-full select-none"
            src="https://d1mdu1cxiv2wrk.cloudfront.net/nyc/images/destinations-Vegas-GettyImages-535829219-s.png"
          />
        ) : (
          <>
            <Map
              agent={agent}
              recommendations={recommendations}
              showChat={showChat}
            />
            <ChatBox
              showChat={showChat}
              setShowChat={setShowChat}
              setShowControlTray={setShowControlTray}
              messages={messages}
              muted={muted}
              setMuted={(muted: boolean) => {
                if (muted) {
                  agent.mute();
                } else {
                  agent.unMute();
                }
              }}
              disabled={false}
              sendMessage={(text: string) => {
                agent.sendMessage({ mime_type: "text/plain", data: text });
                appendMessage({
                  id: resolveRandomId(),
                  role: "user",
                  text,
                  timestamp: Date.now(),
                  raw: { text, role: "user", source: "composer" },
                  mimeType: "text/plain",
                });
              }}
            />
          </>
        )}
        {/* <TATranscriptChat
          className="fixed bottom-2 left-2 z-[200] max-h-[400px] max-w-[300px] bg-black/20 backdrop-blur shadow"
          agent={agent}
          title={connected ? "Agent transcript" : "Connecting…"}

          sendTextUsingAgent
          autoScroll
        /> */}
        <main
          className={`flex-1 flex flex-col justify-center items-center w-full gap-2 relative`}
        >
          {/* Recommendations list moved into Map component and shown there when no selection is open */}

          <div
            className={`flex flex-col flex-none shrink-0 justify-center items-center relative w-full z-[101] text-center pointer-events-none py-4 ${
              connected
                ? "h-auto py-10 !justify-end !w-auto !fixed"
                : "h-full md:flex-1"
            }`}
          >
            {!connected && (
              <div className="mb-9 flex flex-col items-center gap-2 shrink-0">
                <img
                  src="https://d1mdu1cxiv2wrk.cloudfront.net/nyc/images/Ta logo.svg"
                  alt="Tripadvisor AI Logo"
                  className="w-48 md:w-64 lg:w-80"
                />
                <h1 className="text-white text-5xl md:text-8xl font-trip-sans-ultra">
                  {t("app.title")}
                </h1>
              </div>
            )}
            {/* {!connected && (
              <img
                src="https://d1mdu1cxiv2wrk.cloudfront.net/nyc/images/Dark Background@2x.png"
                alt={t("app.optionalHeadline")}
                className="self-start justify-self-start max-h-[400px] w-auto px-10 md:pl-20"
              />
            )} */}

            <section
              className={
                !connected
                  ? "relative flex flex-col items-center justify-center gap-5 shrink-0 max-w-[698px] text-center px-4 md:px-0 "
                  : "fixed bottom-5"
              }
            >
              {!connected && (
                <div className="flex flex-col item-center justify-center text-center leading-10">
                  <h2 className="text-white font-trip-sans-medium text-2xl md:text-3xl mb-1">
                    {t("app.heroQuestion")}
                  </h2>
                  <div className="text-white text-4xl md:text-5xl font-trip-sans-ultra">
                    {t("app.heroTitle")}
                  </div>
                </div>
              )}
              {showControlTray && (
                <ControlTray
                  agent={agent}
                  connected={connected}
                  muted={muted}
                  speakerMuted={speakerMuted}
                  setShowChat={setShowChat}
                />
              )}

              {!connected && (
                <div className="text-white text-md md:text-xl">
                  {t("app.heroDescription")}
                </div>
              )}
            </section>
          </div>
        </main>
        {!connected && <TopBar />}
        {/*!connected && (
          <div className="hidden lg:flex lg:flex-col gap-2 items-center justify-center fixed right-20 bottom-20 w-[150px]">
            <div className="bg-white p-3 w-full">
              <QRCode
                value="https://abudhabi-travel-guide.megastage.ai/?utm_source=qr_code&utm_medium=desktop&utm_campaign=mumbai_event"
                className="w-full h-full"
              />
            </div>
            <span className="text-white font-extrabold text-md xl:text-lg 2xl:text-xl leading-6 text-center">
              {t("app.scanToExplore")}
            </span>
          </div>
        )*/}
      </div>
    </div>
  );
}

export default App;
